/**
 * @fileoverview Timezone-safe date formatting utilities for date-only and datetime ISO strings.
 * @module dateUtils
 *
 * Responsibilities:
 * - Provide a single `formatCalendarDate` helper that formats an ISO date string as a
 *   locale-formatted calendar date WITHOUT shifting the displayed day across timezones.
 * - Detect date-only strings (e.g. `"2026-07-13"`) and parse them as LOCAL midnight so the
 *   calendar day is preserved regardless of the user's UTC offset.
 * - Pass through full datetime strings (with time/timezone components) unchanged.
 *
 * Dependencies:
 * - None (pure JavaScript Date / Intl).
 *
 * Architecture:
 * - PURE functions only; no I/O, no side effects. Safe to call from any UI layer.
 *
 * Notes:
 * - The bug this fixes: `new Date("2026-07-13")` is parsed as UTC midnight per the ECMAScript
 *   spec. `toLocaleDateString` then shifts to the user's local timezone, moving the displayed
 *   calendar day back by one in negative-UTC-offset zones (e.g. US timezones). Appending
 *   `T00:00:00` makes the string a local datetime, so `new Date` parses it as local midnight
 *   and the calendar day is stable.
 * - Full datetime strings that already carry a time component (with or without a `Z` / offset)
 *   are passed through to `new Date` unchanged — their timezone semantics are already explicit.
 */

/** Matches a strict date-only ISO string: `YYYY-MM-DD` with no time or timezone component. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * @reusable
 * Usage: Format a local date for date-only form defaults and values.
 * Rules: Pure presentation helper; does not parse, persist, or mutate dates.
 * Format a Date using its local calendar fields as a date-only ISO string.
 *
 * @param date Date to format; defaults to the current local date.
 * @returns A local `YYYY-MM-DD` string.
 */
export function formatLocalYmd(date: Date = new Date()): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * @reusable
 * Construct a `Date` from an ISO string without the UTC-midnight timezone trap.
 *
 * - Date-only strings (`"2026-07-13"`) are parsed as LOCAL midnight so the calendar day
 *   is preserved in every timezone.
 * - Full datetime strings are passed through to `new Date` unchanged.
 *
 * @param iso The ISO date string (date-only or full datetime).
 * @returns A `Date` whose local calendar day matches the input, or `null` when the input
 *          is empty or cannot be parsed.
 */
export function parseCalendarDate(iso: string | undefined | null): Date | null {
  if (!iso) return null;
  const candidate = DATE_ONLY_RE.test(iso) ? `${iso}T00:00:00` : iso;
  const d = new Date(candidate);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @reusable
 * Format an ISO date string as a locale-formatted calendar date, timezone-safe.
 *
 * - Date-only strings preserve their calendar day in every timezone.
 * - Full datetime strings respect their original timezone semantics.
 *
 * @param iso The ISO date string (date-only or full datetime).
 * @param locale The BCP-47 locale tag. Pass `undefined` (or omit) to use the browser's
 *               default locale, matching `toLocaleDateString()` semantics. Pass an explicit
 *               tag (e.g. `'en-US'`) when a deterministic locale is required.
 * @param options The `Intl.DateTimeFormatOptions` for the output format.
 * @returns The formatted date string, or `fallback` (default `'—'`) when the input is
 *          empty or invalid.
 */
export function formatCalendarDate(
  iso: string | undefined | null,
  locale: string | string[] | undefined = undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  fallback = '—',
): string {
  const d = parseCalendarDate(iso);
  if (!d) return fallback;
  return d.toLocaleDateString(locale, options);
}
